import type { IMFile } from '@wui/im'

import { isImFileImage, isImFileVideo } from '@/shared/utils/files'

export type AttachmentDocumentType = 'spreadsheet' | 'word' | 'markdown' | 'pdf'

export type AttachmentContentKind = 'media' | AttachmentDocumentType

type AttachmentRuleId =
  | 'image'
  | 'video'
  | 'audio'
  | 'spreadsheet'
  | 'word'
  | 'markdown'
  | 'text'
  | 'pdf'
  | 'rtf'

type AttachmentRule = {
  id: AttachmentRuleId
  contentKind?: AttachmentContentKind
  viewable: boolean
  mimeIncludes?: string[]
  mimeEquals?: string[]
  mimes?: RegExp[]
  extensions?: string[]
}

const ATTACHMENT_RULES: AttachmentRule[] = [
  {
    id: 'image',
    contentKind: 'media',
    viewable: true,
    mimeIncludes: ['image/'],
  },
  {
    id: 'video',
    contentKind: 'media',
    viewable: true,
    mimeIncludes: ['video/'],
  },
  {
    id: 'spreadsheet',
    contentKind: 'spreadsheet',
    viewable: true,
    mimeIncludes: ['spreadsheetml'],
    mimeEquals: [
      'application/vnd.ms-excel',
      'application/vnd.oasis.opendocument.spreadsheet',
    ],
    extensions: ['xlsx', 'xlsm', 'xlsb', 'xls', 'ods', 'csv'],
  },
  {
    id: 'word',
    contentKind: 'word',
    viewable: true,
    mimeIncludes: ['wordprocessingml'],
    mimeEquals: ['application/msword'],
    extensions: ['docx', 'doc'],
  },
  {
    id: 'markdown',
    contentKind: 'markdown',
    viewable: true,
    mimeEquals: ['text/markdown'],
    extensions: ['md'],
  },
  {
    id: 'text',
    contentKind: 'markdown',
    viewable: true,
    mimes: [/^(text\/|application\/(json|xml|x-yaml|yaml|javascript))/i],
    extensions: ['txt', 'log', 'ini', 'conf', 'cfg', 'yaml', 'yml', 'json', 'xml', 'csv', 'tsv', 'properties', 'env', 'gitignore'],
  },
  {
    id: 'pdf',
    contentKind: 'pdf',
    viewable: true,
    mimeEquals: ['application/pdf'],
    extensions: ['pdf'],
  },
  {
    id: 'audio',
    viewable: false,
    mimeIncludes: ['audio/'],
  },
  {
    id: 'rtf',
    viewable: false,
    mimeIncludes: ['rtf'],
    extensions: ['rtf'],
  },
]

const getNormalizedMime = (file: IMFile) => (file.filemime ?? '').toLowerCase()

const hasExtension = (filename: string, extensions: string[]) => {
  return new RegExp(`\\.(${extensions.join('|')})$`, 'i').test(filename)
}

const matchesRule = (file: IMFile, rule: AttachmentRule): boolean => {
  const mime = getNormalizedMime(file)

  if (rule.mimeIncludes?.some(part => mime.includes(part))) {
    return true
  }

  if (rule.mimeEquals?.some(value => mime === value.toLowerCase())) {
    return true
  }

  if (rule.mimes?.some(pattern => pattern.test(mime))) {
    return true
  }

  if (rule.extensions?.length && hasExtension(file.filename, rule.extensions)) {
    return true
  }

  return false
}

const findRule = (id: AttachmentRuleId) => {
  return ATTACHMENT_RULES.find(rule => rule.id === id)
}

const matchesRuleId = (file: IMFile, id: AttachmentRuleId) => {
  const rule = findRule(id)
  return rule ? matchesRule(file, rule) : false
}

export const isImageAttachment = (file: IMFile): boolean => isImFileImage(file)

export const isVideoAttachment = (file: IMFile): boolean => isImFileVideo(file)

export const isAudioAttachment = (file: IMFile): boolean => matchesRuleId(file, 'audio')

export const isMediaAttachment = (file: IMFile): boolean => isImageAttachment(file) || isVideoAttachment(file)

export const isSpreadsheetFile = (file: IMFile): boolean => matchesRuleId(file, 'spreadsheet')

export const isWordOfficeFile = (file: IMFile): boolean => matchesRuleId(file, 'word')

export const isMarkdownFile = (file: IMFile): boolean => matchesRuleId(file, 'markdown')

export const isTextFile = (file: IMFile): boolean => matchesRuleId(file, 'text')

export const isRtfFile = (file: IMFile): boolean => matchesRuleId(file, 'rtf')

export const isPdfFile = (file: IMFile): boolean => matchesRuleId(file, 'pdf')

export const resolveAttachmentContentKind = (file: IMFile): AttachmentContentKind | null => {
  // Некоторые вложения (в т.ч. WEBP) приходят с некорректным `filemime`
  // (например, не начинающимся с `image/`), из-за чего правила по `mimeIncludes`
  // не срабатывают и viewer-компонент не монтируется (в итоге картинки "пустые").
  // Для media-навигации ориентируемся на стабильные распознаватели по файлу.
  if (isImFileImage(file) || isImFileVideo(file)) {
    return 'media'
  }

  for (const rule of ATTACHMENT_RULES) {
    if (!rule.viewable || !rule.contentKind) {
      continue
    }
    if (matchesRule(file, rule)) {
      return rule.contentKind
    }
  }
  return null
}
